import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { websiteApi, type ResultsLookup } from "../../shared/api/website";

/** The Website screen's query — shared so both screens read one answer and refresh together. */
export const RESULTS_LOOKUP_KEY = ["website", "results-lookup"] as const;

export function useResultsLookup() {
  return useQuery({
    queryKey: RESULTS_LOOKUP_KEY,
    queryFn: websiteApi.resultsLookup,
    retry: false,
  });
}

type Check = { ok: boolean; text: string };

/**
 * What stands between this clinic and "Cho phép bệnh nhân tra cứu kết quả khám trên website"
 * (#19613). Every unmet condition at once — the switch, the Addy Message connection, each channel's
 * template — read from the same `OtpReadiness` the Website screen and the save use, so this list
 * can never say "đủ điều kiện" for a switch the Website screen would refuse.
 */
export function otpChecks(enabled: boolean, d: ResultsLookup | undefined): Check[] {
  const out: Check[] = [{ ok: enabled, text: "Hành động “Mã xác thực” đang bật" }];
  const otp = d?.otp;
  if (!otp) return out;
  if (otp.reason === "OTP_CHECK_UNAVAILABLE") {
    out.push({ ok: false, text: "Chưa kiểm tra được mẫu OTP (không kết nối được Addy Message)" });
    return out;
  }
  if (otp.messagingConfigured === false) {
    out.push({ ok: false, text: "Kết nối kênh nhắn tin Addy Message" });
    return out;
  }
  const channels = otp.otpChannels ?? [];
  const anyReady = channels.some((c) => c.ready);
  out.push({
    ok: anyReady,
    text: anyReady
      ? "Có mẫu OTP đạt: " +
        channels
          .filter((c) => c.ready)
          .map((c) => (c.channel === "ZBS" ? "Zalo" : c.channel))
          .join(", ")
      : "Chọn ít nhất một mẫu OTP hợp lệ (Zalo hoặc SMS)",
  });
  return out;
}

export function OtpLookupNotice({ enabled }: { enabled: boolean }) {
  const q = useResultsLookup();
  const d = q.data;
  const checks = otpChecks(enabled, d);
  const ready = d?.otp ? enabled && d.otp.available : null;
  const problems = (d?.otp?.otpChannels ?? []).filter((c) => !c.ready && c.problem);

  return (
    <div
      className={
        "grid gap-1.5 rounded-md border p-3 text-sm " +
        (ready === false
          ? "border-amber-300 bg-amber-50 text-amber-950 dark:bg-amber-950/30 dark:text-amber-100"
          : "bg-muted/40")
      }
      data-testid="otp-lookup-notice"
    >
      <p className="font-medium">
        Mã xác thực là điều kiện để bật “Tra cứu kết quả khám trên website”
      </p>
      <p className="text-xs">
        Website chỉ cho bật tra cứu khi hành động này <b>đang bật</b> và có ít nhất một mẫu OTP
        đạt (Zalo hoặc SMS). Tắt hành động này thì website ẩn mục tra cứu.
      </p>
      <ul className="grid gap-0.5 text-xs" data-testid="otp-lookup-checks">
        {checks.map((c) => (
          <li key={c.text} className={c.ok ? "text-emerald-700 dark:text-emerald-400" : ""}>
            {c.ok ? "✓" : "✗"} {c.text}
          </li>
        ))}
        {enabled &&
          problems.map((c) => (
            <li key={c.channel} className="pl-4 text-muted-foreground">
              · {c.problem}
            </li>
          ))}
      </ul>
      {ready === true && (
        <p className="text-xs text-emerald-700 dark:text-emerald-400">
          {/* #19667 — "Nhật ký gửi" was removed from HIS on 13/09; the per-message log lives on
              Addy Message, so the sentence points there instead of at a page that 404s. */}
          Đủ điều kiện gửi mã. Điều này không bảo đảm mọi tin đều tới máy bệnh nhân — khi có
          phản ánh, xem{" "}
          <Link to="/messaging?next=/app/zbs-messages" className="underline">
            tin Zalo
          </Link>{" "}
          hoặc{" "}
          <Link to="/messaging?next=/app/sms-messages" className="underline">
            tin SMS
          </Link>{" "}
          đã gửi trên Addy Message.
        </p>
      )}
      {d && (
        <p className="text-xs">
          Tra cứu kết quả trên website:{" "}
          <b>
            {d.enabled
              ? d.examResults.available
                ? "đang mở"
                : "đã bật nhưng website đang ẩn mục tra cứu"
              : "đang tắt"}
          </b>{" "}
          · <Link to="/website" className="underline">Đi tới Website</Link>
        </p>
      )}
    </div>
  );
}
